import React from 'react';
import { BetButtons, CollectWinningsButton, RoundTimer } from '../BettingComponents';
import { FaArrowUp, FaArrowDown, FaClock, FaTrophy, FaLock, FaInfoCircle, FaEquals, FaTimes, FaUndo } from 'react-icons/fa';
import { ClockIcon } from '@heroicons/react/24/outline';
import { SiTether } from 'react-icons/si';
import { motion } from 'framer-motion';
import { GiBull, GiBearFace } from 'react-icons/gi';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';
import { useGetHistory, useGetCurrentEpoch } from '../../context/PredictionsContext';
import { useBullvsBearContract } from '../../hooks/useBullvsBearContract';
import { useClaimStatus } from '../../hooks/useClaimStatus';
import { useRefundStatus } from '../../hooks/useRefundStatus';
import { formatUnits } from 'viem';

/* global BigInt */

const TREASURY_FEE = 0.10; // 10% taxa da plataforma
const DELAY_FOR_REFUND_SECONDS = 120;

// Auxiliar para converter strings de montante para números, tratando BigInt
const parseAmountToNumber = (amountStr) => {
  if (!amountStr || amountStr === "0" || typeof amountStr === 'undefined') return 0;
  try {
    const bn = BigInt(String(amountStr));
    return bn > 0n ? parseFloat(formatUnits(bn, 6)) : 0;
  } catch (e) {
    return 0;
  }
};

// Auxiliar para calcular payouts potenciais
const calculatePotentialPayouts = (bullAmountStr, bearAmountStr, rewardAmount = 0, rewardBaseCalAmount = 0) => {
  const numBullAmount = parseAmountToNumber(bullAmountStr);
  const numBearAmount = parseAmountToNumber(bearAmountStr);

  let bullPayout = 0;
  let bearPayout = 0;

  //  Use contract logic when rewardAmount is available (for live rounds showing provisional payouts)
  if (rewardAmount > 0 && rewardBaseCalAmount > 0) {
    // Use actual contract reward calculation
    bullPayout = rewardAmount / rewardBaseCalAmount;
    bearPayout = rewardAmount / rewardBaseCalAmount;
  } else {
    // Fallback to estimated calculation
    if (numBullAmount > 0) {
      bullPayout = ((numBullAmount + numBearAmount) / numBullAmount) * (1 - TREASURY_FEE);
    }
    if (numBearAmount > 0) {
      bearPayout = ((numBullAmount + numBearAmount) / numBearAmount) * (1 - TREASURY_FEE);
    }
  }
  
  return { bullPayout: bullPayout || 0, bearPayout: bearPayout || 0 };
};

// Utilitário para detecção do lado vencedor na rodada AO VIVO
const getLiveWinningSide = (oraclePrice, lockPrice) => {
  if (oraclePrice === undefined || lockPrice === undefined || oraclePrice === null || lockPrice === null) return null;
  const o = parseFloat(oraclePrice);
  const l = parseFloat(lockPrice);
  if (isNaN(o) || isNaN(l)) return null;
  if (o > l) return 'up';
  if (o < l) return 'down';
  return 'tie';
};

const RoundCard = ({ round, epoch, status, currentEpoch, oraclePrice, referralAddress }) => {
  const getCardContent = () => {
    switch (status) {
      case 'expired': return <ExpiredRoundCard epoch={epoch} round={round} />;
      case 'live': return <LiveRoundCard epoch={epoch} round={round} oraclePrice={oraclePrice} />;
      case 'next': return <NextRoundCard epoch={epoch} round={round} referralAddress={referralAddress} />;
      case 'later': return <LaterRoundCard epoch={epoch} round={round} />;
      default: return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`w-72 rounded-2xl overflow-hidden backdrop-blur-sm ${status === 'live'
        ? 'border-2 border-[#7900ff] shadow-lg shadow-[#7900ff]/20'
        : 'border border-[#7900ff]/30 hover:border-[#7900ff]/50 transition-all duration-200'
        }`}
    >
      {getCardContent()}
    </motion.div>
  );
};

const PriceDisplay = ({ label, value, color = "white", icon }) => {
  const numValue = Number(value);
  const displayValue = !isNaN(numValue) ? numValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 5 }) : 'N/A';
  return (
    <div className="flex justify-between items-center p-2 rounded-lg bg-black/20 backdrop-blur-sm">
      <span className="text-sm text-gray-400 flex items-center gap-2">
        {icon}
        {label}
      </span>
      <span className={`font-medium ${color.startsWith('text-') ? color : `text-${color}`}`}>
        ${displayValue}
      </span>
    </div>
  );
};


const PayoutCard = ({ type, value, amount }) => {
  const displayPayoutValue = (val) => {
    const num = Number(val);
    if (val !== undefined && val !== null && !isNaN(num) && num > 0) {
      return `${num.toFixed(2)}x`;
    }
    return '0.00x';
  };

 
  if (type === 'up') {
    // console.log('PayoutCard UP - value received:', value);
  }

  const displayTotalAmount = (amt) => {
    if (amt !== undefined && amt !== null) {
      try {
        const formatted = formatUnits(BigInt(String(amt)), 6);
        return `${Number(formatted).toFixed(2)}`;
      } catch (e) {
        return '0.00';
      }
    }
    return '0.00';
  };

  const showAmountSection = amount !== undefined && amount !== null && BigInt(String(amount)) > 0n;

  const isUp = type === 'up';
  const colorClass = isUp ? 'text-green-500' : 'text-red-500';
  const bgClass = isUp ? 'bg-green-500/10' : 'bg-red-500/10';
  const borderClass = isUp ? 'border-green-500/30' : 'border-red-500/30';
  const hoverBgClass = isUp ? 'hover:bg-green-500/20' : 'hover:bg-red-500/20';
  const icon = isUp ? <FaArrowUp className={colorClass} size={10} /> : <FaArrowDown className={colorClass} size={10} />;
  const label = isUp ? 'Bull' : 'Bear';

  return (
    <div className={`${bgClass} ${borderClass} border rounded-xl p-3 ${hoverBgClass} transition-all duration-200 space-y-1.5`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {icon}
          <span className="text-xs font-medium text-gray-300">{label}</span>
        </div>
        <FaInfoCircle size={8} className="text-gray-500 cursor-help" title="Payout after 10% platform fees" />
      </div>

      <div className="bg-black/40 backdrop-blur-sm rounded-lg p-2.5 border border-gray-800/50">
        <div className="flex items-center justify-between">
          <span className={`font-bold text-base ${colorClass}`}>
            {displayPayoutValue(value)}
          </span>
        </div>
      </div>

      {/* Optional: Show pool amount 
      <div className="bg-black/20 backdrop-blur-sm rounded-lg p-2 border border-gray-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className={`font-medium text-xs ${showAmountSection ? 'text-white' : 'text-gray-500'}`}>
              {displayTotalAmount(amount)}
            </span>
            <span className="text-xs text-gray-500">USDT</span>
          </div>
        </div>
      </div> */}
    </div>
  );
};

const CardHeader = ({ round, epoch, label, labelColor, icon }) => (
  <div className="flex items-center gap-2 mb-2">
    <span className="bg-[#7900ff]/10 px-3 py-1 rounded-full text-md text-white">
      #{typeof epoch !== 'undefined' && epoch !== null ? String(epoch) : '?'}
    </span>
    <span className={`${labelColor} px-2 py-1 rounded-full text-md flex items-center gap-1`}>
      {icon}
      {label}
    </span>
  </div>
);

const ExpiredRoundCard = ({ round, epoch }) => {
  const { useUserBet } = useBullvsBearContract();
  const userBetInfo = useUserBet(epoch);

  const numLockPrice = round.lockPrice ? parseFloat(round.lockPrice) : NaN;
  const numClosePrice = round.closePrice ? parseFloat(round.closePrice) : NaN;
  const numBullAmount = parseAmountToNumber(round.bullAmount);
  const numBearAmount = parseAmountToNumber(round.bearAmount);

  const executedTimestamp = parseInt(round.executedTimestamp || '0');
  const closeTimestamp = parseInt(round.closeTimestamp);
  const currentTime = Math.floor(Date.now() / 1000);

  //  Better delayed round detection
  const executionDelay = executedTimestamp > 0 ? executedTimestamp - closeTimestamp : 0;
  const isDelayedExecution = executionDelay > 120; // 2 minutes
  const hasBeenExecuted = executedTimestamp > 0;
  const isOverdue = !hasBeenExecuted && currentTime > closeTimestamp + 120;

  //  Enhanced refund detection logic
  const rewardBaseCalAmount = parseAmountToNumber(round.rewardBaseCalAmount || '0');
  const rewardAmount = parseAmountToNumber(round.rewardAmount || '0');
  const totalBetAmount = numBullAmount + numBearAmount;
  
  // Check if this is a delayed/refund round
  const isDelayedRefund = rewardBaseCalAmount > 0 && Math.abs(rewardBaseCalAmount - totalBetAmount) < 0.01;
  const isTieRefund = rewardBaseCalAmount === 0 && rewardAmount > 0;
  
  //  Also check execution delay for refund eligibility
  const isRefundEligible = isDelayedRefund || isDelayedExecution || isOverdue;


  let actualBullPayout = 0;
  let actualBearPayout = 0;
  let roundOutcomeStatus = 'error';

  //  Check for refund conditions FIRST
  if (isRefundEligible) {
    roundOutcomeStatus = 'refund_eligible';
    actualBullPayout = 0;
    actualBearPayout = 0;
  } else if (isTieRefund) {
    roundOutcomeStatus = 'tie_refund';
    actualBullPayout = 0;
    actualBearPayout = 0;
  } else if (!isNaN(numLockPrice) && !isNaN(numClosePrice)) {
    // Normal price-based outcome logic
    const priceDifference = Math.abs(numClosePrice - numLockPrice);
    const isTie = priceDifference < 0.00001;
    
    if (!isTie && numClosePrice > numLockPrice) {
      roundOutcomeStatus = 'up';
      if (rewardAmount > 0 && round.rewardBaseCalAmount > 0) {
        const userPoolTotal = parseAmountToNumber(round.rewardBaseCalAmount);
        actualBullPayout = rewardAmount / userPoolTotal;
      } else if (numBullAmount > 0) {
        const totalPool = numBullAmount + numBearAmount;
        actualBullPayout = (totalPool / numBullAmount) * (1 - TREASURY_FEE);
      }
      actualBearPayout = 0;
    } else if (!isTie && numClosePrice < numLockPrice) {
      roundOutcomeStatus = 'down';
      if (rewardAmount > 0 && round.rewardBaseCalAmount > 0) {
        const userPoolTotal = parseAmountToNumber(round.rewardBaseCalAmount);
        actualBearPayout = rewardAmount / userPoolTotal;
      } else if (numBearAmount > 0) {
        const totalPool = numBullAmount + numBearAmount;
        actualBearPayout = (totalPool / numBearAmount) * (1 - TREASURY_FEE);
      }
      actualBullPayout = 0;
    } else if (isTie) {
      if (rewardAmount > 0) {
        roundOutcomeStatus = 'tie_refund';
      } else {
        roundOutcomeStatus = 'tie_lose';
      }
      actualBullPayout = 0;
      actualBearPayout = 0;
    }
  } else if (isOverdue && isNaN(numClosePrice)) {
    roundOutcomeStatus = 'refund_eligible';
    actualBullPayout = 0;
    actualBearPayout = 0;
  }

  const userHasEntered = userBetInfo && userBetInfo.amount && BigInt(String(userBetInfo.amount)) > 0n;
  const userPosition = userHasEntered ? (userBetInfo.position === 'up' ? 'up' : 'down') : null;
  const userBetAmountDisplay = userHasEntered ? Number(formatUnits(BigInt(String(userBetInfo.amount)), 6)).toFixed(2) : '0.00';
 
  let userResultDisplayStatus = 'lost';


  if (userHasEntered) {
    if (!userBetInfo) return null;

    // Lógica de status do usuário baseada no contrato
    if (isDelayedRefund || roundOutcomeStatus === 'refund_eligible') {
      userResultDisplayStatus = 'refund';
    } else if (roundOutcomeStatus === 'tie_refund') {
      userResultDisplayStatus = 'push';
    } else if (roundOutcomeStatus === userPosition) {
      userResultDisplayStatus = 'won';
    } else {
      userResultDisplayStatus = 'lost';
    }
  }


  const calculateUserWinnings = () => {
    if (!userHasEntered || !userBetInfo || userResultDisplayStatus !== 'won') return 0;
    
    const userBetAmount = parseAmountToNumber(userBetInfo.amount);
    
    //  Use contract logic for payout calculation
    if (rewardAmount > 0 && round.rewardBaseCalAmount > 0) {
      // This matches the contract's proportion calculation
      const proportion = userBetAmount / parseAmountToNumber(round.rewardBaseCalAmount);
      return proportion * rewardAmount;
    }
    
    // Fallback calculation if rewardAmount not available
    const payout = userPosition === 'up' ? actualBullPayout : actualBearPayout;
    return userBetAmount * payout;
  };

  const userWinnings = calculateUserWinnings();


const calculateRefundPayout = () => {
  if (!userBetInfo || !isRefundEligible) return 0;
  
  const userAmount = parseAmountToNumber(userBetInfo.amount);
  const rewardBaseCalAmount = parseAmountToNumber(round.rewardBaseCalAmount || '0');
  const rewardAmount = parseAmountToNumber(round.rewardAmount || '0');
  

  if (rewardBaseCalAmount > 0 && rewardAmount > 0) {
    return (userAmount / rewardBaseCalAmount) * rewardAmount;
  } else {
    return userAmount * 0.9;
  }
};

const refundPayout = calculateRefundPayout();

  const canCollect = (userResultDisplayStatus === 'won' || userResultDisplayStatus === 'push' || userResultDisplayStatus === 'refund') &&
    userHasEntered &&
    userBetInfo &&
    !userBetInfo.claimed;


  //  Configurações de UI
  let roundOutcomeDisplayText = 'ERROR';
  let roundOutcomeIcon = <FaEquals className="text-gray-500" />;
  let roundOutcomeColor = 'text-gray-500';
  let cardHeaderLabel = 'FINISHED';
  let cardHeaderColor = 'bg-gray-800 text-gray-400';

  if (roundOutcomeStatus === 'refund_eligible') {
    roundOutcomeDisplayText = 'REFUND AVAILABLE';
    roundOutcomeIcon = <FaClock className="text-orange-500" />;
    roundOutcomeColor = 'text-orange-500';
    cardHeaderLabel = 'REFUND AVAILABLE';
    cardHeaderColor = 'bg-orange-500/20 text-orange-400';
  } else if (roundOutcomeStatus === 'up') {
    roundOutcomeDisplayText = 'UP';
    roundOutcomeIcon = <FaArrowUp />;
    roundOutcomeColor = 'text-green-500';
  } else if (roundOutcomeStatus === 'down') {
    roundOutcomeDisplayText = 'DOWN';
    roundOutcomeIcon = <FaArrowDown />;
    roundOutcomeColor = 'text-red-500';
  } else if (roundOutcomeStatus === 'tie_refund') {
    roundOutcomeDisplayText = 'TIE - REFUNDED';
    roundOutcomeIcon = <FaEquals />;
    roundOutcomeColor = 'text-yellow-500';
  } else if (roundOutcomeStatus === 'tie_lose') {
    roundOutcomeDisplayText = 'TIE - HOUSE WINS';
    roundOutcomeIcon = <FaEquals />;
    roundOutcomeColor = 'text-orange-500';
  }


  return (
    <div className={`bg-gradient-to-br from-[#010101] via-[#00102a] to-[#010101] p-5 space-y-4 ${roundOutcomeStatus === 'refund_eligible' ? 'border-2 border-orange-500/50 bg-orange-500/5' : ''
      }`}>
      <div className="flex justify-between items-start">
        <div>
          <CardHeader
            round={round}
            epoch={epoch}
            label={cardHeaderLabel}
            labelColor={cardHeaderColor}
            icon={roundOutcomeStatus === 'refund_eligible' ? <FaClock /> : null}
          />
          <p className={`text-lg font-bold flex items-center gap-2 ${roundOutcomeColor}`}>
            {roundOutcomeIcon}
            {roundOutcomeDisplayText}
          </p>
        </div>
        {userHasEntered && userResultDisplayStatus === 'won' && <FaTrophy className="text-yellow-500 text-xl" />}

      </div>

      <div className="space-y-2">
        <PriceDisplay
          label="Lock Price"
          value={round.lockPrice}
          icon={<FaLock className="text-yellow-500" />}
        />
        <PriceDisplay
          label="Close Price"
          value={round.closePrice}
          color={roundOutcomeStatus === 'refund_eligible' ? 'text-orange-400' : 'text-white'}
          icon={roundOutcomeStatus === 'refund_eligible' ? <FaClock className="text-orange-400" /> : <FaEquals />}
        />
        {roundOutcomeStatus === 'refund_eligible' && (
          <div className="bg-orange-500/10 rounded-lg p-3 border border-orange-500/30">
            <p className="text-sm text-orange-400">
              ⚠️ Round delayed over 2 minutes - eligible for refund (10% platform fee applies)
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <PayoutCard type="up" value={actualBullPayout} amount={round.bullAmount} />
        <PayoutCard type="down" value={actualBearPayout} amount={round.bearAmount} />
      </div>

      <div className="text-xs text-gray-500 text-center">
        {roundOutcomeStatus === 'refund_eligible'
          ? "*refund available for delayed rounds (10% platform fee applies)"
          : "*Payouts include 10% platform fees. House wins on normal ties."
        }
      </div>

      {userHasEntered && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 pt-4 border-t border-gray-800/50"
        >
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-400">Your Position</span>
            <span className={`font-medium px-3 py-0.5 rounded-full text-xs flex items-center gap-1 ${userPosition === 'up' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
              {userPosition === 'up' ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
              {userPosition === 'up' ? 'UP' : 'DOWN'}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm mb-2">
            <span className="text-gray-400">Your Bet</span>
            <span className="flex items-center gap-1 text-white font-medium">
              <SiTether className="text-[#26A17B]" />
              {userBetAmountDisplay} USDT
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-400">Result</span>
            <span className={`font-medium px-3 py-1 rounded-full flex items-center gap-2 ${userResultDisplayStatus === 'won' ? 'bg-green-500/20 text-green-400' :
                userResultDisplayStatus === 'lost' ? 'bg-red-500/20 text-red-400' :
                  'bg-yellow-500/20 text-yellow-400'
              }`}>
              {userResultDisplayStatus === 'won' ? (
                <div className="flex flex-col items-end">
                  <span>🎉 Won</span>
                  <span className="text-xs">{userWinnings.toFixed(2)} USDT</span>
                </div>
              ) :
                userResultDisplayStatus === 'lost' ? '😢 Lost' :
                  'REFUND'}
            </span>
          </div>

          {canCollect && (
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="mt-3"
            >
              <CollectWinningsButton
                round={{ ...round, epoch: epoch }}
                isRefund={userResultDisplayStatus === 'refund' || userResultDisplayStatus === 'push'}
              />
            </motion.div>
          )}
        </motion.div>
      )}
    </div>
  );
};

const LiveRoundCard = ({ round, oraclePrice, epoch }) => {
  const winningSide = getLiveWinningSide(oraclePrice, round.lockPrice);
  const { useUserBet } = useBullvsBearContract();
  const betInfo = useUserBet(epoch);

  const numBullAmount = parseAmountToNumber(round.bullAmount);
  const numBearAmount = parseAmountToNumber(round.bearAmount);

  const userBetAmount = betInfo ? parseAmountToNumber(betInfo.amount || '0') : 0;
  
  let bullPayout = 0;
  let bearPayout = 0;

  //  Winners get the LOSING side's pool, not the total pool
  if (numBullAmount > 0) {
    const userBullAmount = (betInfo?.position === 'up') ? userBetAmount : 0;
    const rewardBaseCalAmount = parseAmountToNumber(round.rewardBaseCalAmount || '0');
    const isUserOnlyBullBettor = userBullAmount > 0 && (rewardBaseCalAmount === 0 || Math.abs(rewardBaseCalAmount - userBullAmount) < 0.01);
    
    if (isUserOnlyBullBettor) {
      // User is the only real bull bettor - gets the bear pool
      bullPayout = (numBearAmount * (1 - TREASURY_FEE)) / userBullAmount;
    } else {
      // Normal calculation - bulls get bear pool distributed proportionally
      bullPayout = (numBearAmount / numBullAmount) * (1 - TREASURY_FEE);
    }
  }

  if (numBearAmount > 0) {
    const userBearAmount = (betInfo?.position === 'down') ? userBetAmount : 0;
    const rewardBaseCalAmount = parseAmountToNumber(round.rewardBaseCalAmount || '0');
    const isUserOnlyBearBettor = userBearAmount > 0 && (rewardBaseCalAmount === 0 || Math.abs(rewardBaseCalAmount - userBearAmount) < 0.01);
    
    if (isUserOnlyBearBettor) {
      // User is the only real bear bettor - gets the bull pool
      bearPayout = (numBullAmount * (1 - TREASURY_FEE)) / userBearAmount;
    } else {
      // Normal calculation - bears get bull pool distributed proportionally
      bearPayout = (numBullAmount / numBearAmount) * (1 - TREASURY_FEE);
    }
  }


  const currentTime = Math.floor(Date.now() / 1000);
  const closeTime = parseInt(round.closeTimestamp);
  const isOverdue = currentTime > closeTime;
  const DELAY_FOR_REFUND_SECONDS = 120; // 2 minutos
  const isRefundEligible = currentTime > closeTime + DELAY_FOR_REFUND_SECONDS;


  const userHasEntered = betInfo && betInfo.amount && BigInt(String(betInfo.amount)) > 0n;
  const userPosition = userHasEntered ? (betInfo.position === 'up' ? 'up' : 'down') : null;
  const userBetAmountDisplay = userHasEntered ? Number(formatUnits(BigInt(String(betInfo.amount)), 6)).toFixed(2) : '0.00';

  let totalPoolDisplay = '0.00 USDT';
  if (round.totalAmount !== undefined && round.totalAmount !== null) {
    try {
      const totalBN = BigInt(String(round.totalAmount));
      totalPoolDisplay = `${Number(formatUnits(totalBN, 6)).toFixed(2)} USDT`;
    } catch (e) {
      totalPoolDisplay = '-- USDT';
    }
  }

  //  Card para round atrasado
  if (isOverdue) {

    return (
      <div className="bg-gradient-to-br from-orange-900/20 to-yellow-900/20 rounded-xl p-6 border border-orange-500/30">
        <CardHeader
          round={round}
          epoch={epoch}
          label={isRefundEligible ? "AWAITING EXECUTION" : "OVERDUE"}
          labelColor="bg-orange-500/20 text-orange-400"
          icon={<ClockIcon className="w-4 h-4" />}
        />

        <div className="text-center mb-4">
          <p className="text-orange-400 font-medium">Round is overdue - waiting for execution</p>
          <p className="text-sm text-gray-400 mt-1">
            {isRefundEligible
              ? "Round is delayed over 2 minutes. Refunds will be available after execution."
              : "Results are provisional until round is executed"}
          </p>
        </div>

        <div className="space-y-2">
          <PriceDisplay
            label="Lock Price"
            value={round.lockPrice}
            icon={<FaLock className="text-yellow-500" />}
          />
          <PriceDisplay
            label="Current Price"
            value={oraclePrice}
            color="text-orange-400"
            icon={<ClockIcon className="w-4 h-4 text-orange-400" />}
          />
        </div>

        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-gray-400">Provisional Winner</span>
          <span className={`font-bold flex items-center gap-1 ${winningSide === 'up'
            ? 'text-green-500'
            : winningSide === 'down'
              ? 'text-red-500'
              : winningSide === 'tie'
                ? 'text-yellow-500'
                : 'text-gray-400'
            }`}>
            {winningSide === 'up' && (<><FaArrowUp /> UP</>)}
            {winningSide === 'down' && (<><FaArrowDown /> DOWN</>)}
            {winningSide === 'tie' && (<><FaEquals /> TIE</>)}
            {!winningSide && winningSide !== 'tie' && '--'}
          </span>
        </div>

        {/* Mostrar posição do usuário se tiver */}
        {userHasEntered && (
          <div className="bg-[#1a1a1a] rounded-lg p-4 border border-orange-500/20 mt-4">
            <h3 className="text-sm font-medium text-gray-400 mb-2">Your Position (Provisional)</h3>
            <div className="flex justify-between items-center">
              <div className={`flex items-center gap-1 font-medium ${userPosition === 'up' ? 'text-green-500' : 'text-red-500'
                }`}>
                {userPosition === 'up' ? <><FaArrowUp /> UP</> : <><FaArrowDown /> DOWN</>}
              </div>
              <p className="text-white font-medium">{userBetAmountDisplay} USDT</p>
            </div>

            {/* Resultado provisório */}
            <div className="mt-3 pt-3 border-t border-gray-800">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-400">Provisional Result</p>
                <div className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded ${winningSide === userPosition
                    ? 'bg-green-500/20 text-green-400'
                    : winningSide === 'tie'
                      ? 'bg-yellow-500/20 text-yellow-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                  {winningSide === userPosition ? 'Winning' :
                    winningSide === 'tie' ? 'Tie' : 'Losing'}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="text-xs text-gray-500 text-center mt-2">
          *Results are provisional. Final results depend on round execution.
        </div>
        <RoundTimer status="live" />
      </div>
    );
  }

  // Card normal para round live
  return (
    <div className="bg-gradient-to-br from-[#010101] via-[#00102a] to-[#010101] p-5 space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <CardHeader
            round={round}
            epoch={epoch}
            label="LIVE"
            labelColor="bg-yellow-500/20 text-yellow-500 animate-pulse"
            icon={<FaClock className="spin-slow" />}
          />
          <p className="text-lg font-bold text-yellow-500 flex items-center gap-2">
            <FaClock className="spin-slow" />
            IN PROGRESS
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <PriceDisplay
          label="Lock Price"
          value={round.lockPrice}
          icon={<FaLock className="text-yellow-500" />}
        />
        <PriceDisplay
          label="Current Price"
          value={oraclePrice}
          color="text-yellow-500"
          icon={<FaClock className="text-yellow-500" />}
        />
      </div>

      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-gray-400">Currently Winning</span>
        <span className={`font-bold flex items-center gap-1 ${winningSide === 'up'
          ? 'text-green-500'
          : winningSide === 'down'
            ? 'text-red-500'
            : winningSide === 'tie'
              ? 'text-gray-400'
              : 'text-gray-400'
          }`}>
          {winningSide === 'up' && (<><FaArrowUp /> UP</>)}
          {winningSide === 'down' && (<><FaArrowDown /> DOWN</>)}
          {winningSide === 'tie' && (<><FaEquals /> TIE</>)}
          {!winningSide && winningSide !== 'tie' && '--'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <PayoutCard type="up" value={bullPayout} amount={round.bullAmount} />
        <PayoutCard type="down" value={bearPayout} amount={round.bearAmount} />
      </div>

      <div className="flex justify-between items-center text-sm mt-3 pt-3 border-t border-gray-800/50">
        <span className="text-gray-400">Total Pool</span>
        <span className="flex items-center gap-1 text-white font-medium">
          <SiTether className="text-[#26A17B]" />
          {totalPoolDisplay}
        </span>
      </div>

      {userHasEntered && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-3 pt-3 border-t border-gray-800/50"
        >
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-400">Your Position</span>
            <span className={`font-medium px-3 py-0.5 rounded-full text-xs flex items-center gap-1 ${userPosition === 'up' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
              {userPosition === 'up' ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
              {userPosition === 'up' ? 'UP' : 'DOWN'}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-400">Your Bet</span>
            <span className="flex items-center gap-1 text-white font-medium">
              <SiTether className="text-[#26A17B]" />
              {userBetAmountDisplay} USDT
            </span>
          </div>
        </motion.div>
      )}
      <div className="text-xs text-gray-500 text-center mt-2">
        *Payouts include 10% platform fees
      </div>
      <RoundTimer status="live" />
    </div>
  );
};

const NextRoundCard = ({ round, epoch, referralAddress }) => {
  const { useUserBet } = useBullvsBearContract();
  const betInfo = useUserBet(epoch);

  const numBullAmount = parseAmountToNumber(round.bullAmount);
  const numBearAmount = parseAmountToNumber(round.bearAmount);

  //  Use the same logic as LiveRoundCard - winners get LOSING side's pool
  const userBetAmount = betInfo ? parseAmountToNumber(betInfo.amount || '0') : 0;
  
  let bullPayout = 0;
  let bearPayout = 0;

  if (numBullAmount > 0) {
    const userBullAmount = (betInfo?.position === 'up') ? userBetAmount : 0;
    const rewardBaseCalAmount = parseAmountToNumber(round.rewardBaseCalAmount || '0');
    const isUserOnlyBullBettor = userBullAmount > 0 && (rewardBaseCalAmount === 0 || Math.abs(rewardBaseCalAmount - userBullAmount) < 0.01);
    
    if (isUserOnlyBullBettor) {
      // User is the only real bull bettor - gets the bear pool
      bullPayout = (numBearAmount * (1 - TREASURY_FEE)) / userBullAmount;
    } else {
      // Normal calculation - bulls get bear pool distributed proportionally
      bullPayout = (numBearAmount / numBullAmount) * (1 - TREASURY_FEE);
    }
  }

  if (numBearAmount > 0) {
    const userBearAmount = (betInfo?.position === 'down') ? userBetAmount : 0;
    const rewardBaseCalAmount = parseAmountToNumber(round.rewardBaseCalAmount || '0');
    const isUserOnlyBearBettor = userBearAmount > 0 && (rewardBaseCalAmount === 0 || Math.abs(rewardBaseCalAmount - userBearAmount) < 0.01);
    
    if (isUserOnlyBearBettor) {
      // User is the only real bear bettor - gets the bull pool
      bearPayout = (numBullAmount * (1 - TREASURY_FEE)) / userBearAmount;
    } else {
      // Normal calculation - bears get bull pool distributed proportionally
      bearPayout = (numBullAmount / numBearAmount) * (1 - TREASURY_FEE);
    }
  }

  const userHasBetNext = betInfo && betInfo.amount && BigInt(String(betInfo.amount)) > 0n;
  const userPositionNext = userHasBetNext ? (betInfo.position === 'up' ? 'up' : 'down') : null;
  const userBetAmountNextDisplay = userHasBetNext ? Number(formatUnits(BigInt(String(betInfo.amount)), 6)).toFixed(2) : '0.00';


  return (
    <div className="bg-gradient-to-br from-[#010101] via-[#00102a] to-[#010101] p-5 space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <CardHeader
            round={round}
            epoch={epoch}
            label="NEXT"
            labelColor="bg-blue-500/20 text-blue-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <PayoutCard type="up" value={bullPayout} amount={round.bullAmount} />
        <PayoutCard type="down" value={bearPayout} amount={round.bearAmount} />
      </div>
      <div className="text-xs text-gray-500 text-center">
        *Payouts include 10% platform fees
      </div>

      {userHasBetNext ? (
        <div className="flex flex-col items-center gap-4">
          {userPositionNext === 'up' ? (
            <>
              <div className="relative">
                <GiBull className="w-16 h-16 text-green-500 animate-pulse" />
                <ArrowTrendingUpIcon className="w-6 h-6 text-green-500 absolute -bottom-1 -right-1" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-green-500 mb-2">Bull Bet Placed!</h3>
                <div className="flex items-center justify-center gap-2 text-gray-400">
                  <SiTether className="w-4 h-4 text-[#26A17B]" />
                  <p>{userBetAmountNextDisplay} USDT</p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="relative">
                <GiBearFace className="w-16 h-16 text-red-500 animate-pulse" />
                <ArrowTrendingDownIcon className="w-6 h-6 text-red-500 absolute -bottom-1 -right-1" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-red-500 mb-2">Bear Bet Placed!</h3>
                <div className="flex items-center justify-center gap-2 text-gray-400">
                  <SiTether className="w-4 h-4 text-[#26A17B]" />
                  <p>{userBetAmountNextDisplay} USDT</p>
                </div>
              </div>
            </>
          )}

          <div className="w-full mt-4 p-4 bg-[#1F2937] rounded-lg">
            <div className="flex items-center justify-center gap-2 text-gray-400">
              <FaClock className="spin-slow" />
              <p className="text-sm">Waiting for round #{String(epoch)} to start...</p>
            </div>
          </div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <BetButtons round={{ ...round, epoch: epoch, referralAddress: referralAddress }} />
        </motion.div>
      )}
      <RoundTimer status="next" />
    </div>
  );
};

const LaterRoundCard = ({ round, epoch }) => {
  const numBullAmount = parseAmountToNumber(round.bullAmount);
  const numBearAmount = parseAmountToNumber(round.bearAmount);

  //  Use the same logic - winners get losing side's pool
  let bullPayout = 0;
  let bearPayout = 0;

  if (numBullAmount > 0) {
    // Bulls get bear pool distributed proportionally
    bullPayout = (numBearAmount / numBullAmount) * (1 - TREASURY_FEE);
  }

  if (numBearAmount > 0) {
    // Bears get bull pool distributed proportionally  
    bearPayout = (numBullAmount / numBearAmount) * (1 - TREASURY_FEE);
  }
  
  return (
    <div className="bg-gradient-to-br from-[#010101] via-[#00102a] to-[#010101] p-5 space-y-4 opacity-70 hover:opacity-100 transition-opacity duration-200">
      <div className="flex justify-between items-center">
        <div>
          <CardHeader
            round={round}
            epoch={epoch}
            label="PENDING"
            labelColor="bg-gray-700 text-gray-300"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <PayoutCard type="up" value={bullPayout} amount={round.bullAmount} />
        <PayoutCard type="down" value={bearPayout} amount={round.bearAmount} />
      </div>
      <div className="text-xs text-gray-500 text-center">
        *Payouts include 10% platform fees
      </div>

      <RoundTimer status="later" />
    </div>
  );
};

export default RoundCard;